import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  PaperPlaneTiltIcon,
  EnvelopeOpenIcon,
  UserFocusIcon,
  MegaphoneSimpleIcon,
} from "@phosphor-icons/react";

/**
 * Admin-only CTA cluster for the weekly drop digest.
 * - Preview (dry-run counts, no sends)
 * - Send to me (admin's inbox only)
 * - Send to all (real blast, with 2-step confirmation modal)
 */
export default function DigestActions() {
  const [busy, setBusy] = useState(null); // "preview" | "test" | "all"
  const [summary, setSummary] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fire = async (endpoint, busyKey, successLabel) => {
    setBusy(busyKey);
    setSummary(null);
    try {
      const { data } = await api.post(`/admin/digest/${endpoint}`);
      setSummary(data);
      toast.success(successLabel);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="rounded-3xl border border-zinc-200 bg-white p-5 sm:p-6"
      data-testid="digest-actions-card"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-2xl bg-[#1D78AB]/10 text-[#1D78AB] grid place-items-center flex-shrink-0">
          <MegaphoneSimpleIcon size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-black text-lg tracking-tight">
              Weekly Drop Digest
            </h3>
            <span className="pill pill-dark text-[10px]">AUTO · SUNDAY 9AM PT</span>
          </div>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
            Hand-picked 6-listing email to every opted-in user. Runs automatically
            each Sunday — or fire it manually below.
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        <Button
          variant="outline"
          className="h-10 justify-start gap-2 text-sm border-zinc-300 hover:border-[#1D78AB] hover:text-[#1D78AB]"
          disabled={!!busy}
          onClick={() => fire("preview", "preview", "Preview calculated — no emails sent.")}
          data-testid="digest-preview-btn"
        >
          <EnvelopeOpenIcon size={15} weight="fill" />
          {busy === "preview" ? "Previewing…" : "Preview (dry run)"}
        </Button>

        <Button
          variant="outline"
          className="h-10 justify-start gap-2 text-sm border-zinc-300 hover:border-[#1D78AB] hover:text-[#1D78AB]"
          disabled={!!busy}
          onClick={() => fire("send-test", "test", "Test sent to your inbox.")}
          data-testid="digest-send-test-btn"
        >
          <UserFocusIcon size={15} weight="fill" />
          {busy === "test" ? "Sending…" : "Send to me (test)"}
        </Button>

        <Button
          className="h-10 justify-start gap-2 text-sm bg-[#1D78AB] hover:bg-[#165d85] text-white"
          disabled={!!busy}
          onClick={() => setConfirmOpen(true)}
          data-testid="digest-send-all-btn"
        >
          <PaperPlaneTiltIcon size={15} weight="fill" />
          {busy === "all" ? "Blasting…" : "Send to all"}
        </Button>
      </div>

      {summary && (
        <div
          className="mt-5 rounded-2xl bg-zinc-50 border border-zinc-200 p-4 text-sm"
          data-testid="digest-summary"
        >
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="font-semibold text-zinc-900">
              {summary.dry_run
                ? "Preview result"
                : summary.target_user_id
                ? "Test send result"
                : "Blast result"}
            </div>
            <code className="font-mono text-[10px] text-zinc-500">
              {summary.run_id}
            </code>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryStat
              label="Candidates"
              value={summary.candidates}
              testId="digest-stat-candidates"
            />
            <SummaryStat
              label="Sent"
              value={summary.sent}
              tone="emerald"
              testId="digest-stat-sent"
            />
            <SummaryStat
              label="No listings"
              value={summary.skipped_no_listings}
              tone="zinc"
              testId="digest-stat-skipped"
            />
            <SummaryStat
              label="Opted out"
              value={summary.skipped_optout}
              tone="zinc"
              testId="digest-stat-optout"
            />
          </div>
          {summary.errors > 0 && (
            <div
              className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
              data-testid="digest-errors"
            >
              {summary.errors} send error{summary.errors === 1 ? "" : "s"} —
              check backend logs.
            </div>
          )}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="digest-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Blast the weekly digest now?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends the 6-listing drop digest to every user whose marketing
              preference is ON. Users who opted out via their preference page
              will be skipped automatically. This cannot be undone — make sure
              you have fresh, active listings live.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="digest-confirm-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-[#1D78AB] hover:bg-[#165d85] text-white"
              onClick={() =>
                fire("send-all", "all", "Digest sent to all opted-in users.")
              }
              data-testid="digest-confirm-send"
            >
              Yes, send to all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SummaryStat({ label, value, tone = "slate", testId }) {
  const toneMap = {
    slate: "text-zinc-900",
    emerald: "text-emerald-700",
    zinc: "text-zinc-500",
  };
  return (
    <div data-testid={testId}>
      <div
        className={`font-display font-black text-2xl leading-none ${toneMap[tone] || toneMap.slate}`}
      >
        {value ?? 0}
      </div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mt-1">
        {label}
      </div>
    </div>
  );
}
